import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  CertificateCard,
  CertificateHeader,
  CertificateBody,
  CertificateFooter,
  LoadingContainer,
  ErrorContainer,
  ValidBadge,
  InvalidBadge,
  CertificateInfo,
  UserInfo,
  CourseInfo,
  ValidationStatus,
  BackButton,
} from './styles';
import { ICertificateDTO } from '../../dtos/ICertificateValidationDTO';
import api from '../../services/api';

const Certificate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState<ICertificateDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCertificate = useCallback(async () => {
    if (!id) {
      setError('ID do certificado não fornecido');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const API_KEY = process.env.REACT_APP_CERTIFICATION_API_KEY;
      const response = await api.get(`/certificates/validate/${id}`, {
        headers: {
          authorization: API_KEY,
        },
      });
      setCertificate(response.data.certificate);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Certificado não encontrado');
      } else {
        setError('Erro ao validar certificado. Tente novamente.');
      }
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCertificate();
  }, [fetchCertificate]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleBackClick = (): void => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <div className="spinner" />
          <p>Validando certificado...</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <h2>⚠️ Erro na Validação</h2>
          <p>{error}</p>
          <BackButton onClick={handleBackClick}>Voltar</BackButton>
        </ErrorContainer>
      </Container>
    );
  }

  if (!certificate) {
    return (
      <Container>
        <ErrorContainer>
          <h2>❌ Certificado Inválido</h2>
          <p>Este certificado não pôde ser validado ou não existe.</p>
          <BackButton onClick={handleBackClick}>Voltar</BackButton>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      {certificate && (
        <CertificateCard>
          <CertificateHeader>
            <h1>Validação de Certificado</h1>
            <ValidationStatus>
              {certificate.is_valid ? (
                <ValidBadge>✅ Certificado Válido</ValidBadge>
              ) : (
                <InvalidBadge>❌ Certificado Inválido</InvalidBadge>
              )}
            </ValidationStatus>
          </CertificateHeader>

          <CertificateBody>
            <CertificateInfo>
              <div className="info-group">
                <strong>Data de Emissão:</strong>
                <span>{formatDate(certificate.issued_at)}</span>
              </div>
            </CertificateInfo>

            <UserInfo>
              <h3>Informações do Aluno</h3>
              <div className="info-group">
                <strong>Nome:</strong>
                <span>{certificate.user_name}</span>
              </div>
              <div className="info-group">
                <strong>Email:</strong>
                <span>{certificate.user_email}</span>
              </div>
            </UserInfo>

            <CourseInfo>
              <h3>Informações do Curso</h3>
              <div className="info-group">
                <strong>Nome do Curso:</strong>
                <span>{certificate.course_name}</span>
              </div>
              <div className="info-group">
                <strong>Descrição:</strong>
                <span>{certificate.course_description}</span>
              </div>
              <div className="info-group">
                <strong>Carga Horária:</strong>
                <span>{certificate.course_duration_hours} horas</span>
              </div>
              <div className="info-group">
                <strong>Instrutor:</strong>
                <span>{certificate.instructor_name}</span>
              </div>
            </CourseInfo>
          </CertificateBody>

          <CertificateFooter>
            <p>
              Este certificado foi emitido pela <strong>Fuzzy Makers</strong> e
              pode ser verificado através do código:{' '}
              <strong>{certificate.id}</strong>
            </p>
            <BackButton onClick={handleBackClick}>Voltar</BackButton>
          </CertificateFooter>
        </CertificateCard>
      )}
    </Container>
  );
};

export default Certificate;
